import { Injectable, Signal } from "@angular/core";

import { Observable, Subject } from "rxjs";

import { NotificationDto } from "../../models/notifications/notification.interface";
import { EventStreamFrame } from "../../utils/event-stream-parser.util";
import { createAuthenticatedEventStream } from "../event-stream/authenticated-event-stream";

/** Sem `/user`: esta rota escapa do Ocelot e é hospedada pelo próprio Gateway. */
const STREAM_PATH = "api/notifications/stream";
const CREATED_EVENT = "notification.created";

/**
 * Canal de tempo real das notificações (DEV-872), por SSE.
 *
 * A conexão (fetch autenticado fora dos interceptors, leitura, parser, backoff com jitter,
 * 401/403/404 e troca de identidade) é do `createAuthenticatedEventStream`; este serviço só nomeia o
 * caminho e interpreta `notification.created`.
 *
 * Contrato de confiabilidade: a listagem HTTP é a fonte de verdade; este canal é best-effort. Queda
 * inesperada é NORMAL — o servidor derruba a conexão de propósito quando o buffer de 64 envelopes
 * enche — e não é estado de erro visível ao usuário.
 */
@Injectable({ providedIn: "root" })
export class NotificationsStreamService {
  private readonly createdSubject = new Subject<NotificationDto>();
  private readonly connection = createAuthenticatedEventStream({
    path: STREAM_PATH,
    onFrame: (frame) => this.emitFrame(frame)
  });

  /**
   * Minor 7 (fix round final): sem consumidor de PRODUÇÃO — nada renderiza um indicador de
   * "conectado". Fica mesmo assim: é a única superfície observável de fora para quatro
   * regressões gravadas na suíte (achado 2 do round 2, achados 2 e 3 do round 3, e o teste base
   * "reports connected...") que fixam bugs reais da máquina de estado de abort/retry. Sem este
   * signal, esses testes teriam de inferir o mesmo fato por `resyncCount`/contagem de `fetch`, uma
   * evidência mais fraca do exato modo de falha que documentam.
   */
  public readonly connected: Signal<boolean> = this.connection.connected;
  /** `false` = a feature não existe para esta sessão (403 de papel, 404 de produto). Esconde o sino. */
  public readonly available: Signal<boolean> = this.connection.available;

  public readonly created: Observable<NotificationDto> = this.createdSubject.asObservable();
  /** Emitido a cada conexão aberta. Não há replay: o consumidor recarrega lista E contador. */
  public readonly resync: Observable<void> = this.connection.resync;
  /** Emitido quando a identidade muda (troca de perfil ou personificação). O consumidor descarta
   *  todo o conteúdo da identidade anterior. */
  public readonly identityChanged: Observable<void> = this.connection.identityChanged;

  public start(): void {
    this.connection.start();
  }

  public stop(): void {
    this.connection.stop();
  }

  private emitFrame(frame: EventStreamFrame): void {
    // Heartbeat (a cada 20s) e comentário não têm nada a fazer aqui.
    if (frame.event !== CREATED_EVENT || !frame.data) {
      return;
    }
    try {
      this.createdSubject.next(JSON.parse(frame.data) as NotificationDto);
    } catch {
      console.warn("DEV-872: frame de notificação com JSON inválido descartado.");
    }
  }
}
